<template>
  <VSnackbar
    v-if="consentStore.needsBanner"
    :model-value="true"
    :timeout="-1"
    location="bottom"
    variant="elevated"
    color="surface"
    class="consent-banner"
  >
    <div class="d-flex flex-wrap align-center ga-3">
      <VAvatar
        color="lightprimary"
        rounded="md"
        size="36"
        class="flex-shrink-0"
      >
        <Icon
          icon="solar:shield-check-line-duotone"
          color="primary"
          height="20"
        />
      </VAvatar>
      <span class="flex-grow-1 text-body-2">
        {{ t('consent.message') }}
        <a
          href="/legal/confidentialite"
          class="text-primary text-decoration-underline ml-1"
          @click.prevent="goToPrivacy"
        >{{ t('consent.privacy_link') }}</a>
      </span>
      <div class="d-flex ga-2 flex-shrink-0">
        <VBtn
          variant="text"
          size="small"
          @click="handleDecline"
        >
          {{ t('consent.decline') }}
        </VBtn>
        <VBtn
          color="primary"
          variant="tonal"
          size="small"
          @click="handleAccept"
        >
          {{ t('consent.accept') }}
        </VBtn>
      </div>
    </div>
  </VSnackbar>
</template>

<script setup lang="ts">
/**
 * @component ConsentBanner
 * @description RGPD consent banner for PostHog analytics.
 * Shows only when the user has not yet responded to the consent prompt.
 * Non-blocking — fixed at bottom, does not prevent page interaction.
 */
import { Icon } from '@iconify/vue'
import { useConsentStore } from '@/stores/consentStore'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

const consentStore = useConsentStore()
const router = useRouter()
const { t } = useI18n()

function handleAccept(): void {
  consentStore.grantConsent()
}

function handleDecline(): void {
  consentStore.revokeConsent()
}

function goToPrivacy(): void {
  void router.push('/legal/confidentialite')
}
</script>

<style scoped>
/*
Bande fine plutôt que carte flottante en coin (héritage `location="bottom end"`
+ `multi-line`, ~110px de haut) : à cette taille, la bannière recouvrait
littéralement des boutons "Enregistrer" en bas de page (ex. onglet Compte des
Paramètres) tant qu'un visiteur ne l'avait pas acceptée/refusée — reproduit et
vérifié en conditions réelles (Playwright, pas juste un artefact de test).
Une bande pleine largeur ~1 ligne réduit fortement la zone de recouvrement.
*/
.consent-banner :deep(.v-overlay__content) {
  max-width: min(900px, calc(100% - 32px));
}

/* On mobile, pages like /reserve show a fixed bottom CTA bar (z-index 50);
this snackbar's own z-index sits well above it, so without this offset it
covers the pay button. Push the banner up to clear that bar everywhere,
since it's mounted globally and has no page-specific awareness. 112px =
bar height including the discount line + iOS safe area. */
@media (max-width: 1023.98px) {
  .consent-banner :deep(.v-overlay__content) {
    margin-bottom: 112px;
  }
}

/* En dessous de ~600px, chaque ligne compte : le texte + les deux boutons ne
tiennent de toute façon pas sur une ligne, donc la bannière retombe sur 3
niveaux (icône+texte, puis actions). Icône masquée et espacements resserrés
pour limiter la hauteur totale — sur un petit écran, elle peut sinon recouvrir
un bouton primaire en bas de page (ex. "Se connecter"), vérifié en conditions
réelles. */
@media (max-width: 599.98px) {
  .consent-banner :deep(.v-snackbar__content) {
    padding: 10px 12px;
  }

  .consent-banner .v-avatar {
    display: none;
  }
}
</style>
